import React, {useState} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack, Tooltip, Typography, useTheme} from "@mui/material";
import {DeleteRounded, ExpandMore, FileUploadRounded, DownloadRounded, CreateRounded} from "@mui/icons-material";
import {useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import {idEntityState, RFMContractorDataValueState} from "../model/state";
import {FormAutocomplete, FormDatePicker, FormTextField} from "../../common/form-fields-wrappers";
import {AxiosError} from "axios";
import { yupResolver } from "@hookform/resolvers/yup";
import { CONTRACTOR_DOCUMENTS_FORM_VALIDATION_SCHEMA } from "../utils/config";
import { insertDocumentsContractor } from "../service/contractorSearch.actions";
import DocumentalHistory from "../../documental-history/DocumentalHistory";
import AssociatedDmsList from "./AssociatedDmsList";

const DocumentRepository = () => {
    const theme = useTheme();
    const typeList = ["Contract", "Warranty", "Other"];
    const [contractorDataState, setContractorDataState] = useRecoilState(RFMContractorDataValueState);
    const [isDisabled, setIsDisabled] = useState(true);
    const [getId, setId] = useRecoilState(idEntityState);
    const [openHistory, setOpenHistory] = useState(false);
    const handleOpenHistory = () => setOpenHistory(true);
    const handleCloseHistory = () => setOpenHistory(false);
    const [update, setUpdate] = useState(true);

    const handleDisableForm = (value:boolean) => {
        setIsDisabled(value);
    }

    const {t} = useTranslation(['network']);
    const documentsForm = useForm({
        defaultValues: {
            documents: contractorDataState.documents
        },
        resolver: yupResolver(CONTRACTOR_DOCUMENTS_FORM_VALIDATION_SCHEMA)
    });

    const onSubmit = (data: any) => {
        const reqParameters = {
            id: getId,
            // documents: data.documents.map((d: any) => ({
            //     ...d,
            //     validityStartDate : d.validityStartDate.format("YYYY-MM-DD"),
            //     validityEndDate : d.validityEndDate.format("YYYY-MM-DD")
            // }))
            documents: data.documents
        }

        insertDocumentsContractor(reqParameters)
            .then( (res:any) => {
                const newValue = {...contractorDataState, documents: res.documents};
                setContractorDataState(newValue);
            })
            .catch((err: AxiosError) => {});
        setIsDisabled(true);
    }

    const documentsArray = useFieldArray({name: 'documents', control: documentsForm.control});

    const handleAddDocument = () => {
        documentsArray.append({
            title: "Other",
            file: "",
            note: "",
            fileName: "",
            validityStartDate: null,
            validityEndDate: null
        });
    }

    const handleOnDelete = (index?: number) => {
        documentsArray.remove(index);
    }

    const handleFileOnChange = (e: React.BaseSyntheticEvent, field: any, index: number) => {
        e.stopPropagation();
        if (!!e.target.files[0]?.size) {
            let reader = new FileReader();
            reader.readAsDataURL(e.target.files[0]);
            reader.onload = function () {
                documentsForm.setValue(field, reader.result);
                documentsForm.trigger(field);
                documentsForm.setValue(`documents.${index}.fileName`, e.target.files[0].name);
                documentsForm.trigger(field);
                setUpdate(!update);
            };
        }
    }

    const downloadFunc = (data: string, fileName?: string) => {
        const linkSource = data;
        const downloadLink = document.createElement('a');
        downloadLink.href = linkSource;
        downloadLink.download = fileName ? fileName : 'file';
        downloadLink.click();
    };
    const downloadDocument = (base64: string | ArrayBuffer, fileName?: string) => {
        downloadFunc(base64.toString(), fileName);
    };

    const dropChanges = () => {
        documentsArray.replace(contractorDataState.documents);
        setIsDisabled(true);
        setUpdate(!update);
    }
    
    return (
        <>
                    <Accordion defaultExpanded={true}>
                        <AccordionSummary expandIcon={<ExpandMore/>}>
                            <Typography variant={"h6"}>
                                {t("contractorMng.managerRFM.documentRepository.accordion.contractorSection")}
                            </Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                            <form onSubmit={documentsForm.handleSubmit(onSubmit)}>
                                <Grid item xs={12}>
                                    <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                                        { isDisabled &&
                                            <Tooltip title={t("contractorMng.managerRFM.documentRepository.buttons.edit")}>
                                                <IconButton sx={{mx: theme.spacing(0.5)}} color={"accent"} onClick={() => {handleDisableForm(false)}}>
                                                    <CreateRounded />
                                                </IconButton>
                                        </Tooltip>
                                        }
                                    </Stack>
                                </Grid>
                                {documentsArray.fields.length === 0 &&
                                    <Grid item container xs={12} md={12} align-items="center" justifyContent="flex">
                                        <p>{t("contractorMng.managerRFM.documentRepository.messages.noDocument")}</p>
                                    </Grid>
                                }
                                {documentsArray.fields.map((field, index) => {
                                    return (
                                        <Grid key={field.id} item container md={12} xs={12} >

                                            {isDisabled === false ?
                                                <>
                                                <Grid container item md={10} xs={10} columnSpacing={2}>
                                                    <Grid item md={3} xs={3}>
                                                        <FormAutocomplete
                                                            disabled={isDisabled}
                                                            name={`documents.${index}.title`}
                                                            control={documentsForm.control}
                                                            options={typeList}
                                                            label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.title")}
                                                        />
                                                    </Grid>

                                                    <Grid item md={3} xs={3}>
                                                        <FormTextField
                                                            disabled={isDisabled}
                                                            control={documentsForm.control}
                                                            name={`documents.${index}.note`}
                                                            label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.note")}
                                                        />
                                                    </Grid>

                                                    <Grid item md={3} xs={3}>
                                                        <FormDatePicker
                                                            disabled={isDisabled}
                                                            control={documentsForm.control}
                                                            name={`documents.${index}.validityStartDate`}
                                                            label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.validityStartDate")}
                                                        />
                                                    </Grid>

                                                    <Grid item md={3} xs={3}>
                                                        <FormDatePicker
                                                            disabled={isDisabled}
                                                            control={documentsForm.control}
                                                            name={`documents.${index}.validityEndDate`}
                                                            label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.validityEndDate")}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                    <Grid item xs={2} md={2} sx={{display: "flex", justifyContent: "center"}}>
                                                        {documentsForm.getValues(`documents.${index}.file`) !== "" &&
                                                            <IconButton color="secondary" component="label" sx={{alignSelf: "end"}}>
                                                                <input hidden
                                                                       type="file"
                                                                       onChange={(e: React.SyntheticEvent) => handleFileOnChange(e, `documents.${index}.file`, index)}
                                                                />
                                                                <FileUploadRounded/>
                                                            </IconButton>}

                                                        { documentsForm.getValues(`documents.${index}.file`) === "" && <IconButton color="error" component="label" sx={{alignSelf: "end"}}>
                                                            <input hidden accept="image/*"
                                                                   type="file"
                                                                   onChange={(e: React.SyntheticEvent) => handleFileOnChange(e, `documents.${index}.file`, index)}
                                                            />
                                                            <FileUploadRounded/>
                                                        </IconButton>}


                                                        { documentsForm.getValues(`documents.${index}.file`) !== "" ?
                                                            <Box sx={{
                                                                display: "flex",
                                                                justifyContent: "flex-end",
                                                                alignSelf: "end"
                                                            }}>
                                                                <IconButton color="secondary"
                                                                            onClick={(e: React.SyntheticEvent) => downloadDocument(documentsForm.getValues(`documents.${index}.file`), documentsForm.getValues(`documents.${index}.title`))}>
                                                                    <DownloadRounded/>
                                                                </IconButton>
                                                            </Box>
                                                            : null}

                                                        <Box sx={{
                                                            display: "flex",
                                                            justifyContent: "flex-end",
                                                            alignSelf: "end"
                                                        }}>
                                                            <IconButton color="secondary" onClick={() => handleOnDelete(index)}>
                                                                <DeleteRounded/>
                                                            </IconButton>
                                                        </Box>
                                                    </Grid>
                                                </>

                                            : <Grid container item md={12} xs={12} columnSpacing={2}>
                                                    <Grid item md={3} xs={3}>
                                                        <FormAutocomplete
                                                            disabled={isDisabled}
                                                            name={`documents.${index}.title`}
                                                            control={documentsForm.control}
                                                            options={typeList}
                                                            label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.title")}
                                                        />
                                                    </Grid>

                                                    <Grid item md={3} xs={3}>
                                                        <FormTextField
                                                            disabled={isDisabled}
                                                            control={documentsForm.control}
                                                            name={`documents.${index}.note`}
                                                            label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.note")}
                                                        />
                                                    </Grid>

                                                    <Grid item md={3} xs={3}>
                                                        <FormDatePicker
                                                            disabled={isDisabled}
                                                            control={documentsForm.control}
                                                            name={`documents.${index}.validityStartDate`}
                                                            label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.validityStartDate")}
                                                        />
                                                    </Grid>

                                                    <Grid item md={3} xs={3}>
                                                        <FormDatePicker
                                                            disabled={isDisabled}
                                                            control={documentsForm.control}
                                                            name={`documents.${index}.validityEndDate`}
                                                            label={t("contractorMng.managerRFM.documentRepository.documentsArray.enabled.validityEndDate")}
                                                        />
                                                    </Grid>
                                                </Grid>}
                                        </Grid>
                                    );
                                })}
                            {
                                isDisabled === false &&
                                <Grid container sx={{display: 'flex', justifyContent: "flex-end", alignItems: "flex-end"}}  >
                                    <Grid xs={12} md={6} item sx={{display: 'flex', justifyContent: {xs: 'center', md: 'flex-start'}, gap: {xs: 1, md: 2}, flexDirection: {xs: 'column', md: 'row'}, padding: {xs: 1, md: 0}}}>
                                        <Button color="accent" onClick={handleAddDocument}>
                                            {t("contractorMng.managerRFM.documentRepository.documentsArray.buttons.addDocument")}
                                        </Button>
                                        <Button onClick={handleOpenHistory}>
                                            {t("contractorMng.managerRFM.documentRepository.documentsArray.buttons.archive")}
                                        </Button>
                                    </Grid>
                                    <Grid xs={12} md={6} item sx={{display: 'flex', justifyContent: {xs: 'center', md: 'flex-end'}, gap: {xs: 1, md: 2}, flexDirection: {xs: 'column', md: 'row'}, padding: {xs: 1, md: 0}}}>
                                        <Button onClick={dropChanges}>
                                            {t("contractorMng.managerRFM.documentRepository.documentsArray.buttons.cancel")}
                                        </Button>
                                        <Button onClick={documentsForm.handleSubmit(onSubmit)}>
                                            {t("contractorMng.managerRFM.documentRepository.documentsArray.buttons.confirm")}
                                        </Button>
                                    </Grid>
                                </Grid>
                            }
                        </form>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion defaultExpanded={true}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant={"h6"}>
                                {t("contractorMng.managerRFM.documentRepository.accordion.previousAssociatedDmsData")}
                            </Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                            <AssociatedDmsList list={contractorDataState.associatedDms.filter((x: any) => x.primaryDms === false)} />
                        </AccordionDetails>
                    </Accordion>

            <Dialog
                open={openHistory}
                onClose={handleCloseHistory}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    {t('contractorMng.managerRFM.documentRepository.dialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DocumentalHistory functionality={"CONTR"} id={contractorDataState.id} documentsTitles={typeList}/>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseHistory}>{t('contractorMng.managerRFM.documentRepository.dialog.buttons.cancel')}</Button>
                </DialogActions>
            </Dialog>

        </>
    )
}

export default DocumentRepository;

